import { FC, useMemo, useState } from 'react';
import { Collapse } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import DOMPurify from 'dompurify';
import { Alert } from '@dynamon-as/ui';
import { cx } from '@lib/utils';
import type { ErrorEntry } from '@typings';
import styles from './ErrorPanel.module.css';

interface ErrorPanelProps {
    entries: ErrorEntry[];
}

const summarize = (errorCount: number, warningCount: number): string => {
    const parts: string[] = [];
    if (errorCount > 0) {
        parts.push(errorCount === 1 ? '1 problem' : `${errorCount} problemer`);
    }
    if (warningCount > 0) {
        parts.push(
            warningCount === 1 ? '1 advarsel' : `${warningCount} advarsler`,
        );
    }
    return parts.join(', ');
};

const ErrorPanel: FC<ErrorPanelProps> = ({ entries }) => {
    const [isOpen, isSetOpen] = useState(false);

    const {
        errorCount,
        warningCount,
    }: { errorCount: number; warningCount: number } = useMemo(() => {
        let errors = 0;
        let warnings = 0;
        for (const e of entries) {
            if (e.type === 'error') {
                errors++;
            } else {
                warnings++;
            }
        }
        return { errorCount: errors, warningCount: warnings };
    }, [entries]);

    if (entries.length === 0) {
        return null;
    }

    const isError = errorCount > 0;

    return (
        <div
            className={cx(
                styles.errorPanel,
                isError ? styles.hasError : styles.hasWarning,
            )}
            role="alert"
            aria-live="polite"
        >
            <button
                type="button"
                className={styles.header}
                onClick={() => isSetOpen((prev) => !prev)}
                aria-expanded={isOpen}
            >
                <span className={styles.summary}>
                    {summarize(errorCount, warningCount)}
                </span>
                {isOpen ? <ExpandLess /> : <ExpandMore />}
            </button>
            <Collapse in={isOpen}>
                <div className={styles.list}>
                    {entries.map((entry, i) => (
                        <Alert
                            key={i}
                            type={entry.type}
                        >
                            {entry.html && typeof entry.content === 'string' ? (
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(
                                            entry.content,
                                        ),
                                    }}
                                />
                            ) : (
                                entry.content
                            )}
                        </Alert>
                    ))}
                </div>
            </Collapse>
        </div>
    );
};

export default ErrorPanel;
